import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { App as AppNativa } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Lock } from 'lucide-react';
import { useAuth } from './AuthContext';
import { esApp } from '../utils/plataforma';
import { Logo } from '../components/Logo';
import { NOMBRE_APP } from '../config/marca';
import { recordarAjusteDispositivo } from '../utils/preferencias';

/**
 * Bloqueo de la app con Face ID / Touch ID (solo en la app de iOS).
 *
 * Es opcional y de cada dispositivo: se activa en Configuración → Perfil y se
 * guarda en el propio iPhone. La cuenta recuerda que lo querías, para
 * ofrecerlo al reinstalar o en otro iPhone (src/utils/preferencias.ts). Con él activado, la app pide
 * Face ID al abrirse y al volver tras más de GRACIA_MS en segundo plano. Al
 * salir de la app, el contenido se tapa siempre, para que no se vean los
 * saldos en el selector de apps.
 */

const CLAVE = 'bloqueoBiometrico';
const GRACIA_MS = 60_000;

interface BloqueoContextType {
  /** El dispositivo tiene Face ID o Touch ID configurado (y estamos en la app). */
  disponible: boolean;
  /** 'Face ID' o 'Touch ID', para los textos. */
  nombreBiometria: string;
  activo: boolean;
  /** Ya se sabe si hay biometría y si estaba activado. */
  listo: boolean;
  /** Pide Face ID una vez antes de activarlo: así se sabe que funciona. */
  activar: () => Promise<void>;
  desactivar: () => Promise<void>;
}

const BloqueoContext = createContext<BloqueoContextType | undefined>(undefined);

export const BloqueoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [disponible, setDisponible] = useState(false);
  const [nombreBiometria, setNombreBiometria] = useState('Face ID');
  const [activo, setActivo] = useState(false);
  // Cargando la preferencia: hasta saberla, se tapa por si acaso
  const [listo, setListo] = useState(!esApp);
  const [bloqueado, setBloqueado] = useState(false);
  const [tapado, setTapado] = useState(false);
  const [error, setError] = useState('');
  const salidaRef = useRef(0);

  const verificar = useCallback(async () => {
    await NativeBiometric.verifyIdentity({
      reason: `Desbloquear ${NOMBRE_APP}`,
      title: `Desbloquear ${NOMBRE_APP}`,
      useFallback: true,   // si Face ID falla, el código del iPhone
    });
  }, []);

  const desbloquear = useCallback(async () => {
    setError('');
    try {
      await verificar();
      setBloqueado(false);
    } catch {
      setError('No se ha podido comprobar que eres tú. Vuelve a intentarlo.');
    }
  }, [verificar]);

  // Al arrancar: ¿hay biometría y estaba activado?
  useEffect(() => {
    if (!esApp) return;
    (async () => {
      try {
        const r = await NativeBiometric.isAvailable({ useFallback: true });
        setDisponible(r.isAvailable);
        setNombreBiometria(r.biometryType === BiometryType.TOUCH_ID ? 'Touch ID' : 'Face ID');
        const { value } = await Preferences.get({ key: CLAVE });
        const on = value === '1' && r.isAvailable;
        setActivo(on);
        setBloqueado(on);
      } catch {
        setDisponible(false);
      } finally {
        setListo(true);
      }
    })();
  }, []);

  // Al abrir con el bloqueo puesto, pedir Face ID sin tener que pulsar nada
  useEffect(() => {
    if (bloqueado && user) desbloquear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloqueado, !!user]);

  // Segundo plano: tapar siempre; al volver, bloquear si pasó la gracia
  useEffect(() => {
    if (!esApp) return;
    const escucha = AppNativa.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        salidaRef.current = Date.now();
        setTapado(true);
        return;
      }
      setTapado(false);
      if (activo && Date.now() - salidaRef.current >= GRACIA_MS) setBloqueado(true);
    });
    return () => { escucha.then(e => e.remove()); };
  }, [activo]);

  const activar = async () => {
    await verificar();
    await Preferences.set({ key: CLAVE, value: '1' });
    setActivo(true);
    recordarAjusteDispositivo({ bloqueo: true });
  };

  const desactivar = async () => {
    await Preferences.remove({ key: CLAVE });
    setActivo(false);
    recordarAjusteDispositivo({ bloqueo: false });
    setBloqueado(false);
  };

  // Sin sesión no hay nada que proteger (la pantalla de entrada ya lo es)
  const mostrarBloqueo = esApp && user && (!listo || bloqueado);
  const mostrarTapa = esApp && user && tapado && !mostrarBloqueo;

  return (
    <BloqueoContext.Provider value={{ disponible, nombreBiometria, activo, listo, activar, desactivar }}>
      {children}
      {(mostrarBloqueo || mostrarTapa) && (
        <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6
                        seguro-arriba seguro-abajo text-center">
          <Logo size={64} />
          <p className="mt-4 text-lg font-bold text-gray-900 dark:text-white">{NOMBRE_APP}</p>
          {mostrarBloqueo && listo && (
            <>
              <button onClick={desbloquear}
                className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700
                           text-white font-semibold">
                <Lock className="w-5 h-5" /> Desbloquear con {nombreBiometria}
              </button>
              {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400 max-w-xs">{error}</p>}
            </>
          )}
        </div>
      )}
    </BloqueoContext.Provider>
  );
};

export const useBloqueo = () => {
  const ctx = useContext(BloqueoContext);
  if (!ctx) throw new Error('useBloqueo debe usarse dentro de BloqueoProvider');
  return ctx;
};
